import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Truck, AlertTriangle, Shield, Wrench, QrCode, Package,
  ShoppingCart, DollarSign, RefreshCw, Search, Filter,
  CheckCircle, XCircle, Clock, MapPin, User, Calendar,
  TrendingUp, TrendingDown, Eye, Printer, ChevronDown,
  ChevronUp, Zap, Target, Activity, FileText, Lock,
  Unlock, AlertCircle, BarChart2, PieChart as PieIcon,
  Settings, Download
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, Cell, PieChart, Pie,
  ComposedChart, Line, Area
} from 'recharts';

// ── Constants ──────────────────────────────────────────────────────────────
const API = (import.meta as any).env?.VITE_API_URL ||
  'https://nexus360-platform-backend-api-production.up.railway.app';

const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899'];

// ── Types ──────────────────────────────────────────────────────────────────
interface Asset {
  asset_id:string; asset_type:string; vehicle_registration:string;
  region:string; unit:string; purchase_date:string; purchase_cost:number;
  current_mileage:number; last_maintenance_date:string;
  fuel_consumption_liters:number; fuel_expected_liters:number;
  operational_hours:number; maintenance_cost_ytd:number;
  engine_temp_warning:boolean|string; oil_pressure_warning:boolean|string;
  brake_wear_pct:number; brand:string; model:string; status:string;
  condition:string; category:string; assigned_officer:string;
  assigned_city:string; assigned_precinct:string; fuel_type:string;
  fuel_capacity_l:number; last_fuel_date:string; last_fuel_quantity_l:number;
  last_inspection_date:string; next_inspection_date:string;
  inspection_result:string; repair_cost:number; repair_duration_days:number;
  gps_latitude:number; gps_longitude:number; year_manufactured:number;
  loss_type:string; decommission_date:string;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const n  = (v:any,fb=0)=>{const x=Number(v);return isNaN(x)?fb:x;};
const bl = (v:any)=>v===true||String(v).toLowerCase()==='true';
const fm = (v:number)=>v>=1e9?(v/1e9).toFixed(1)+'B':v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':String(Math.round(v));
const ff = (v:number)=>fm(v)+' F';
const dSince = (d:string)=>d?Math.floor((Date.now()-new Date(d).getTime())/864e5):999;
const dUntil = (d:string)=>d?Math.floor((new Date(d).getTime()-Date.now())/864e5):-999;

// Amortissement linéaire (5 ans par défaut)
const netBookValue = (cost:number, purchaseDate:string, years=5)=>{
  const age = dSince(purchaseDate)/365;
  const remaining = Math.max(0, 1 - age/years);
  return Math.round(cost * remaining);
};

// TCO
const tco = (a:Asset)=>
  n(a.purchase_cost) + n(a.maintenance_cost_ytd)*3 + n(a.repair_cost);

// Fuel anomaly: >30% vs expected
const fuelAnomaly = (a:Asset)=>{
  const exp = n(a.fuel_expected_liters);
  const act = n(a.fuel_consumption_liters);
  if(exp<=0) return 0;
  return Math.round((act-exp)/exp*100);
};

// Mileage anomaly: >20% vs category average
const mileageAnomaly = (a:Asset, avgMileage:number)=>{
  const m = n(a.current_mileage);
  if(avgMileage<=0) return 0;
  return Math.round((m-avgMileage)/avgMileage*100);
};

// Alert 30 days before next inspection
const inspectionAlert = (a:Asset)=>{
  const days = dUntil(a.next_inspection_date);
  return days>=0 && days<=30;
};

// Long repair: >60 days
const longRepair = (a:Asset)=>
  a.status==='En réparation' && n(a.repair_duration_days)>60;

// ── Mock weapons data ──────────────────────────────────────────────────────
const WEAPONS_MOCK = [
  {id:'ARM-001',type:'Pistolet semi-auto',serial:'PSA-2019-0042',brand:'Beretta',model:'92FS',caliber:'9mm',issued_to:'Commissaire MBALLA Jean',unit:'Commissariat Central Yaoundé',region:'Centre',issue_date:'2024-01-15',return_due:'2025-01-15',returned:true,return_date:'2025-01-14',condition:'Bon',signature:'MBALLA-J-2024-001',notes:''},
  {id:'ARM-002',type:'Fusil d\'assaut',serial:'FA-2020-0018',brand:'FAMAS',model:'G2',caliber:'5.56mm',issued_to:'Lt. NGUEMA Paul',unit:'Unité Intervention Douala',region:'Littoral',issue_date:'2024-03-01',return_due:'2025-03-01',returned:false,return_date:'',condition:'Excellent',signature:'NGUEMA-P-2024-002',notes:'Mission terrain en cours'},
  {id:'ARM-003',type:'Pistolet semi-auto',serial:'PSA-2021-0091',brand:'Glock',model:'17',caliber:'9mm',issued_to:'Insp. FOMBA Aïcha',unit:'Brigade Mobile Nord',region:'Nord',issue_date:'2023-11-20',return_due:'2024-11-20',returned:false,return_date:'',condition:'Moyen',signature:'FOMBA-A-2023-003',notes:'ALERTE: retard restitution 30j+'},
  {id:'ARM-004',type:'Carabine',serial:'CAR-2022-0007',brand:'Mossberg',model:'500',caliber:'12 gauge',issued_to:'Adj. BELL Marie',unit:'Commissariat Bafoussam',region:'Ouest',issue_date:'2024-06-10',return_due:'2025-06-10',returned:true,return_date:'2025-06-09',condition:'Bon',signature:'BELL-M-2024-004',notes:''},
  {id:'ARM-005',type:'Pistolet semi-auto',serial:'PSA-2020-0156',brand:'Beretta',model:'M9',caliber:'9mm',issued_to:'Commissaire BELLO Ibrahim',unit:'Commissariat Garoua',region:'Nord',issue_date:'2024-02-28',return_due:'2025-02-28',returned:true,return_date:'2025-02-27',condition:'Excellent',signature:'BELLO-I-2024-005',notes:''},
  {id:'ARM-006',type:'Fusil d\'assaut',serial:'FA-2019-0033',brand:'AK-103',model:'AK-103',caliber:'7.62mm',issued_to:'Sgt. OWONA David',unit:'Unité Spéciale Bertoua',region:'Est',issue_date:'2023-08-15',return_due:'2024-08-15',returned:false,return_date:'',condition:'Mauvais',signature:'OWONA-D-2023-006',notes:'ALERTE CRITIQUE: retard >120j, arme non restituée'},
  {id:'ARM-007',type:'Pistolet semi-auto',serial:'PSA-2022-0203',brand:'SIG Sauer',model:'P226',caliber:'9mm',issued_to:'Capitaine NKOA Sylvie',unit:'Direction Centrale Yaoundé',region:'Centre',issue_date:'2024-09-01',return_due:'2025-09-01',returned:false,return_date:'',condition:'Excellent',signature:'NKOA-S-2024-007',notes:'Attribution long terme — DG approuvée'},
  {id:'ARM-008',type:'Carabine',serial:'CAR-2021-0044',brand:'Remington',model:'870',caliber:'12 gauge',issued_to:'Insp. MOUSSA Hamidou',unit:'Commissariat Maroua',region:'Extrême-Nord',issue_date:'2024-04-20',return_due:'2025-04-20',returned:true,return_date:'2025-04-19',condition:'Bon',signature:'MOUSSA-H-2024-008',notes:''},
];


// ── ML Helpers (aligned with LogisticsIntelligencePage) ───────────────────
const failureProbability = (a:Asset):number => {
  const brake  = Math.min(n(a.brake_wear_pct)/100,1);
  const engW   = bl(a.engine_temp_warning)?1:0;
  const oilW   = bl(a.oil_pressure_warning)?1:0;
  const days   = Math.min(dSince(a.last_maintenance_date)/365,1);
  const fuelR  = n(a.fuel_expected_liters)>0
    ? Math.min((n(a.fuel_consumption_liters)-n(a.fuel_expected_liters))/n(a.fuel_expected_liters),1)
    : 0;
  const raw = brake*0.30+engW*0.25+oilW*0.20+days*0.15+fuelR*0.10;
  return Math.round(100/(1+Math.exp(-8*(raw-0.5)))*10)/10;
};


// ── Wrapped X-axis tick (up to 3 lines, no rotation) ────────────────────
const WrappedTick = ({x,y,payload}:any) => {
  const label = String(payload?.value||'');
  // Split into chunks of max 12 chars on word boundaries
  const words = label.split(' ');
  const lines: string[] = [];
  let cur = '';
  words.forEach(w => {
    if((cur+' '+w).trim().length <= 12) {
      cur = (cur+' '+w).trim();
    } else {
      if(cur) lines.push(cur);
      cur = w;
    }
  });
  if(cur) lines.push(cur);
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text key={i} x={0} y={0} dy={10 + i*11}
          textAnchor="middle" fill="#94a3b8" fontSize={8}>{line}</text>
      ))}
    </g>
  );
};

// ── QR Code SVG Generator (simple matrix) ────────────────────────────────
const QRCodeSVG = ({value,size=120}:{value:string;size?:number})=>{
  const hash = value.split('').reduce((a,c)=>((a<<5)-a)+c.charCodeAt(0),0);
  const cells = 21;
  const cell = size/cells;
  const matrix:boolean[][] = Array.from({length:cells},(_,r)=>
    Array.from({length:cells},(_,c)=>{
      if(r<7&&c<7) return !((r===1||r===5)&&c>=1&&c<=5)&&!(c===1||c===5);
      if(r<7&&c>13) return !((r===1||r===5)&&c>=15&&c<=19)&&!(c===15||c===19);
      if(r>13&&c<7) return !((r===15||r===19)&&c>=1&&c<=5)&&!(c===1||c===5);
      return ((Math.abs(hash*(r*cells+c+1))%3)===0);
    })
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <rect width={size} height={size} fill="white"/>
      {matrix.map((row,r)=>row.map((on,c)=>on?(
        <rect key={`${r}-${c}`} x={c*cell} y={r*cell} width={cell} height={cell} fill="#1e293b"/>
      ):null))}
    </svg>
  );
};

// ── Tab type ───────────────────────────────────────────────────────────────
type Tab = 'inventory'|'fleet'|'antifraud'|'weapons'|'costs';

const TABS:{id:Tab;label:string;icon:any;color:string}[] = [
  {id:'inventory',  label:'Inventaire QR',      icon:QrCode,     color:'text-blue-400'},
  {id:'fleet',      label:'Parc Automobile',     icon:Truck,      color:'text-green-400'},
  {id:'antifraud',  label:'Anti-Détournements',  icon:AlertTriangle,color:'text-red-400'},
  {id:'weapons',    label:'Registre Armements',  icon:Shield,     color:'text-purple-400'},
  {id:'costs',      label:'Couts et TCO',          icon:DollarSign, color:'text-cyan-400'},
];

// ── Tooltip ────────────────────────────────────────────────────────────────
const NexTip = ({active,payload,label}:any)=>{
  if(!active||!payload?.length) return null;
  return(
    <div className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs">
      <p className="text-white font-semibold mb-1">{label}</p>
      {payload.map((p:any,i:number)=>(
        <div key={i} className="flex justify-between gap-3">
          <span className="text-slate-300">{p.name}</span>
          <span className="font-bold" style={{color:p.fill||p.stroke}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ── QR Modal ──────────────────────────────────────────────────────────────
const QRModal = ({asset,onClose}:{asset:Asset;onClose:()=>void})=>{
  const nbv = netBookValue(n(asset.purchase_cost), asset.purchase_date);
  const handlePrint = ()=>{
    const w = window.open('','_blank','width=400,height=600');
    if(!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Fiche QR — ${asset.asset_id}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px;font-size:11px;color:#1a1a2e;}
    h2{font-size:14px;margin-bottom:4px;}.qr{text-align:center;margin:12px 0;}
    table{width:100%;border-collapse:collapse;}td{padding:4px 6px;border-bottom:1px solid #e2e8f0;}
    .label{color:#64748b;font-weight:600;width:45%;}
    @media print{body{-webkit-print-color-adjust:exact;}}</style></head><body>
    <h2>DGSN — Fiche Actif ${asset.asset_id}</h2>
    <p style="color:#64748b;font-size:10px;">Généré le ${new Date().toLocaleDateString('fr-FR')}</p>
    <div class="qr"><img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(asset.asset_id+'|'+asset.vehicle_registration+'|'+asset.region)}" width="150" height="150"/></div>
    <table>
      <tr><td class="label">ID Actif</td><td>${asset.asset_id}</td></tr>
      <tr><td class="label">Désignation</td><td>${asset.asset_type} ${asset.brand} ${asset.model}</td></tr>
      <tr><td class="label">Immatriculation</td><td>${asset.vehicle_registration}</td></tr>
      <tr><td class="label">Valeur nette comptable</td><td>${ff(nbv)}</td></tr>
      <tr><td class="label">Localisation GPS</td><td>${asset.gps_latitude?.toFixed(4)}, ${asset.gps_longitude?.toFixed(4)}</td></tr>
      <tr><td class="label">Ville / Unité</td><td>${asset.assigned_city} — ${asset.unit}</td></tr>
      <tr><td class="label">État physique</td><td>${asset.condition}</td></tr>
      <tr><td class="label">Responsable</td><td>${asset.assigned_officer}</td></tr>
      <tr><td class="label">Date acquisition</td><td>${asset.purchase_date}</td></tr>
      <tr><td class="label">Coût d'acquisition</td><td>${ff(n(asset.purchase_cost))}</td></tr>
      <tr><td class="label">Durée amortissement</td><td>5 ans (linéaire)</td></tr>
      <tr><td class="label">Statut</td><td>${asset.status}</td></tr>
    </table>
    </body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),500);
  };

  return(
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <motion.div initial={{scale:.9,opacity:0}} animate={{scale:1,opacity:1}}
        className="bg-slate-900 border border-blue-900/40 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-bold text-lg">{asset.asset_id}</h3>
            <p className="text-slate-400 text-xs">{asset.asset_type} — {asset.brand} {asset.model}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XCircle className="w-5 h-5"/>
          </button>
        </div>

        <div className="flex justify-center mb-4 bg-white rounded-xl p-3">
          <QRCodeSVG value={`${asset.asset_id}|${asset.vehicle_registration}|${asset.region}|${asset.assigned_officer}`} size={140}/>
        </div>

        <div className="space-y-1.5 text-xs mb-4">
          {[
            ['Désignation', `${asset.asset_type} ${asset.brand} ${asset.model}`],
            ['Immatriculation', asset.vehicle_registration],
            ['Valeur nette comptable', ff(nbv)],
            ['Localisation GPS', `${asset.gps_latitude?.toFixed(4)}, ${asset.gps_longitude?.toFixed(4)}`],
            ['Ville / Unité', `${asset.assigned_city} — ${asset.unit}`],
            ['État physique', asset.condition],
            ['Responsable', asset.assigned_officer],
            ['Date acquisition', asset.purchase_date],
            ['Coût acquisition', ff(n(asset.purchase_cost))],
            ['Durée amortissement', '5 ans (linéaire)'],
            ['Statut', asset.status],
          ].map(([k,v])=>(
            <div key={k} className="flex justify-between gap-2 py-1 border-b border-slate-800">
              <span className="text-slate-400">{k}</span>
              <span className="text-white font-medium text-right">{v}</span>
            </div>
          ))}
        </div>

        <button onClick={handlePrint}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 text-sm hover:bg-blue-500/30 transition-colors">
          <Printer className="w-4 h-4"/>Imprimer la fiche QR
        </button>
      </motion.div>
    </motion.div>
  );
};

// ── MAIN PAGE ──────────────────────────────────────────────────────────────
export const LogisticsManagementPage = ({user}:{user:any})=>{
  const [assets,   setAssets]   = useState<Asset[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState<Tab>('inventory');
  const [search,   setSearch]   = useState('');
  const [qrAsset,  setQrAsset]  = useState<Asset|null>(null);
  const [regionF,  setRegionF]  = useState('ALL');
  const [statusF,  setStatusF]  = useState('ALL');
  const [expanded, setExpanded] = useState<string|null>(null);

  const fetchAssets = useCallback(async()=>{
    try{
      const r = await fetch(`${API}/api/assets`);
      const j = await r.json();
      if(j.data?.length) setAssets(j.data.map((a:any)=>({
        ...a,
        purchase_cost:n(a.purchase_cost), current_mileage:n(a.current_mileage),
        fuel_consumption_liters:n(a.fuel_consumption_liters),
        fuel_expected_liters:n(a.fuel_expected_liters),
        operational_hours:n(a.operational_hours),
        maintenance_cost_ytd:n(a.maintenance_cost_ytd),
        brake_wear_pct:n(a.brake_wear_pct), fuel_capacity_l:n(a.fuel_capacity_l),
        last_fuel_quantity_l:n(a.last_fuel_quantity_l),
        repair_cost:n(a.repair_cost), repair_duration_days:n(a.repair_duration_days),
        gps_latitude:n(a.gps_latitude), gps_longitude:n(a.gps_longitude),
        year_manufactured:n(a.year_manufactured),
      })));
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[]);

  useEffect(()=>{fetchAssets();},[fetchAssets]);
  useEffect(()=>{const t=setInterval(fetchAssets,30000);return()=>clearInterval(t);},[fetchAssets]);

  const filtered = useMemo(()=>assets.filter(a=>{
    const s = search.toLowerCase();
    const matchS = !s||[a.asset_id,a.asset_type,a.brand,a.model,a.vehicle_registration,a.assigned_officer,a.region].some(v=>String(v).toLowerCase().includes(s));
    const matchR = regionF==='ALL'||a.region===regionF;
    const matchSt = statusF==='ALL'||a.status===statusF;
    return matchS&&matchR&&matchSt;
  }),[assets,search,regionF,statusF]);

  // Category averages for anomaly detection
  const catAvgMileage = useMemo(()=>{
    const m:Record<string,number[]>={};
    assets.forEach(a=>{if(!m[a.category])m[a.category]=[];m[a.category].push(n(a.current_mileage));});
    return Object.fromEntries(Object.entries(m).map(([k,v])=>[k,v.reduce((a,b)=>a+b,0)/v.length]));
  },[assets]);

  // ── Fraud alerts ──
  const fraudAlerts = useMemo(()=>assets.filter(a=>{
    const fuel = fuelAnomaly(a);
    const miles = mileageAnomaly(a, catAvgMileage[a.category]||0);
    return fuel>30 || miles>30 || longRepair(a);
  }),[assets,catAvgMileage]);

  // ── Inspection alerts (30 days) ──
  const inspAlerts = useMemo(()=>assets.filter(inspectionAlert),[assets]);

  // ── Weapon alerts ──
  const weaponAlerts = WEAPONS_MOCK.filter(w=>!w.returned&&
    dUntil(w.return_due)<0);

  // ── TCO by category ──
  const tcoByCategory = useMemo(()=>{
    const m:Record<string,{total:number;count:number;purchase:number;maint:number;repair:number}> = {};
    assets.forEach(a=>{
      const cat = a.category||a.asset_type||'Autre';
      if(!m[cat]) m[cat]={total:0,count:0,purchase:0,maint:0,repair:0};
      m[cat].total += tco(a);
      m[cat].purchase += n(a.purchase_cost);
      m[cat].maint += n(a.maintenance_cost_ytd)*3;
      m[cat].repair += n(a.repair_cost);
      m[cat].count++;
    });
    return Object.entries(m).map(([cat,d])=>({
      cat:cat.length>18?cat.slice(0,18)+'…':cat,
      avgTCO:Math.round(d.total/d.count/1e6*10)/10,
      purchase:Math.round(d.purchase/d.count/1e6*10)/10,
      maint:Math.round(d.maint/d.count/1e6*10)/10,
      repair:Math.round(d.repair/d.count/1e6*10)/10,
      count:d.count,
    })).sort((a,b)=>b.avgTCO-a.avgTCO).slice(0,8);
  },[assets]);

  // ── Procurement analysis ──
  const procurementData = useMemo(()=>{
    const m:Record<string,{totalMaint:number;totalRepair:number;count:number}> = {};
    assets.forEach(a=>{
      const cat = a.category||a.asset_type||'Autre';
      if(!m[cat]) m[cat]={totalMaint:0,totalRepair:0,count:0};
      m[cat].totalMaint += n(a.maintenance_cost_ytd);
      m[cat].totalRepair += n(a.repair_cost);
      m[cat].count++;
    });
    return Object.entries(m).map(([cat,d])=>({
      cat:cat.length>16?cat.slice(0,16)+'…':cat,
      avgMaint:Math.round(d.totalMaint/d.count/1e3),
      avgRepair:Math.round(d.totalRepair/d.count/1e3),
      count:d.count,
      savingsEstimate:Math.round(d.totalMaint*0.12/1e3),
    })).sort((a,b)=>b.savingsEstimate-a.savingsEstimate).slice(0,8);
  },[assets]);

  const totalProcurementSavings = procurementData.reduce((s,d)=>s+d.savingsEstimate,0);

  if(loading) return(
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <Truck className="w-12 h-12 text-blue-400 animate-pulse"/>
      <span className="text-slate-400">Chargement module gestion logistique…</span>
    </div>
  );

  return(
    <div className="p-4 md:p-6 space-y-4">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center justify-center">
            <Truck className="w-5 h-5 text-green-400"/>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Gestion Intelligente du Parc</h1>
            <p className="text-slate-400 text-xs">Inventaire · Parc auto · Anti-fraude · Armements · Achats · TCO</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {fraudAlerts.length>0&&<span className="px-2 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/30 animate-pulse">{fraudAlerts.length} alertes fraude</span>}
          {inspAlerts.length>0&&<span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">{inspAlerts.length} échéances 30j</span>}
          {weaponAlerts.length>0&&<span className="px-2 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">{weaponAlerts.length} armes non restituées</span>}
          <button onClick={fetchAssets} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs transition-colors">
            <RefreshCw className="w-3.5 h-3.5"/>Actualiser
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex flex-wrap gap-1 bg-slate-800/60 rounded-xl p-1 border border-blue-900/30">
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${tab===t.id?'bg-slate-700 text-white shadow':'text-slate-400 hover:text-white'}`}>
            <t.icon className={`w-3.5 h-3.5 ${tab===t.id?t.color:''}`}/>
            {t.label}
            {t.id==='antifraud'&&fraudAlerts.length>0&&<span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-red-500/30 text-red-300">{fraudAlerts.length}</span>}
            {t.id==='fleet'&&inspAlerts.length>0&&<span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-yellow-500/30 text-yellow-300">{inspAlerts.length}</span>}
            {t.id==='weapons'&&weaponAlerts.length>0&&<span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-purple-500/30 text-purple-300">{weaponAlerts.length}</span>}
          </button>
        ))}
      </div>

      {/* ── Filters (shared) ── */}
      {(tab==='inventory'||tab==='fleet'||tab==='antifraud'||tab==='tco')&&(
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Rechercher actif, immatriculation, officier…"
              className="w-full pl-8 pr-3 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs placeholder-slate-500 focus:outline-none focus:border-blue-500/60"/>
          </div>
          <select value={regionF} onChange={e=>setRegionF(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60">
            <option value="ALL">Toutes régions</option>
            {['Adamaoua','Centre','Est','Extrême-Nord','Littoral','Nord','Nord-Ouest','Ouest','Sud','Sud-Ouest'].map(r=><option key={r}>{r}</option>)}
          </select>
          <select value={statusF} onChange={e=>setStatusF(e.target.value)}
            className="px-2 py-1.5 bg-slate-700/60 border border-slate-600/40 rounded-lg text-white text-xs focus:outline-none focus:border-blue-500/60">
            <option value="ALL">Tous statuts</option>
            {['Opérationnel','En maintenance','Hors service','En réparation'].map(s=><option key={s}>{s}</option>)}
          </select>
        </div>
      )}

      <AnimatePresence mode="wait">

        {/* ══════ TAB: INVENTAIRE QR ══════ */}
        {tab==='inventory'&&(
          <motion.div key="inventory" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="space-y-3">
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                {label:'Total actifs',value:assets.length,color:'text-blue-400',bg:'bg-blue-500/10',border:'border-blue-500/30',icon:Package},
                {label:'Valeur nette totale',value:ff(assets.reduce((s,a)=>s+netBookValue(n(a.purchase_cost),a.purchase_date),0)),color:'text-green-400',bg:'bg-green-500/10',border:'border-green-500/30',icon:DollarSign},
                {label:'Coût acquisition total',value:ff(assets.reduce((s,a)=>s+n(a.purchase_cost),0)),color:'text-yellow-400',bg:'bg-yellow-500/10',border:'border-yellow-500/30',icon:TrendingUp},
                {label:'Actifs amortis',value:assets.filter(a=>netBookValue(n(a.purchase_cost),a.purchase_date)===0).length,color:'text-red-400',bg:'bg-red-500/10',border:'border-red-500/30',icon:AlertCircle},
              ].map((k,i)=>(
                <motion.div key={i} whileHover={{scale:1.03}} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:i*.05}}
                  className={`rounded-xl p-3 border ${k.border} ${k.bg} flex items-center gap-3`}>
                  <k.icon className={`w-5 h-5 ${k.color} flex-shrink-0`}/>
                  <div><p className={`font-bold text-base ${k.color}`}>{k.value}</p><p className="text-slate-400 text-xs">{k.label}</p></div>
                </motion.div>
              ))}
            </div>

            {/* Asset table */}
            <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden">
              <div className="px-4 py-3 bg-slate-700/60 border-b border-blue-900/40 flex items-center justify-between">
                <span className="text-sm font-semibold text-white">Inventaire numérique complet ({filtered.length} actifs)</span>
                <span className="text-xs text-slate-400">Cliquer sur QR pour fiche imprimable</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-blue-900/40 bg-slate-700/60">
                      {['ID Actif','Désignation','Immat.','N° Série','Catég.','Année','Région','Unité','Ville','Commissariat','VNC (FCFA)','État','Condition','En service depuis','Km','Heures','Carburant','Capa.(L)','Der. ravit.','Qté (L)','Der. maint.','Coût maint.','Moteur⚠','Huile⚠','Freins %','Der. insp.','Proch. insp.','Résultat insp.','Coût répa.','Durée répa.','Perte/Vol','Déclassement','Responsable','Email','Acquisition','Amorti','Statut','QR'].map(h=>(
                        <th key={h} className="px-3 py-2 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0,50).map((a,i)=>{
                      const nbv = netBookValue(n(a.purchase_cost), a.purchase_date);
                      const amortized = nbv===0;
                      return(
                        <motion.tr key={a.asset_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.01}}
                          className={`border-b border-blue-900/30 hover:bg-slate-700/50 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-blue-300 font-mono font-bold whitespace-nowrap">{a.asset_id}</td>
                          <td className="px-3 py-2 text-white whitespace-nowrap">{a.asset_type} {a.brand} {a.model}</td>
                          <td className="px-3 py-2 text-slate-300 font-mono whitespace-nowrap">{a.vehicle_registration||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 font-mono">{a.serial_number||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.category||a.asset_type||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{a.year_manufactured||'—'}</td>
                          <td className="px-3 py-2 text-slate-300">{a.region||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 max-w-40 truncate" title={a.unit}>{a.unit||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.assigned_city||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap max-w-40 truncate" title={a.assigned_precinct}>{a.assigned_precinct||'—'}</td>
                          <td className={`px-3 py-2 font-bold whitespace-nowrap ${amortized?'text-red-400':'text-green-400'}`}>{ff(nbv)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${a.condition==='Excellent'?'text-green-400':a.condition==='Bon'?'text-blue-400':a.condition==='Moyen'?'text-yellow-400':'text-red-400'}`}>{a.condition||'—'}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.condition||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{(a.operational_since||'').slice(0,10)||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 text-right whitespace-nowrap">{a.current_mileage?Number(a.current_mileage).toLocaleString()+' km':'—'}</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{a.operational_hours||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.fuel_type||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{a.fuel_capacity_l||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{(a.last_fuel_date||'').slice(0,10)||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{a.last_fuel_quantity_l||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{(a.last_maintenance_date||'').slice(0,10)||'—'}</td>
                          <td className="px-3 py-2 text-yellow-400 text-right whitespace-nowrap">{a.maintenance_cost_ytd?ff(n(a.maintenance_cost_ytd)):'—'}</td>
                          <td className="px-3 py-2 text-center">{bl(a.engine_temp_warning)?<span className="text-red-400 font-bold">⚠️</span>:<span className="text-green-400">✓</span>}</td>
                          <td className="px-3 py-2 text-center">{bl(a.oil_pressure_warning)?<span className="text-red-400 font-bold">⚠️</span>:<span className="text-green-400">✓</span>}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`font-bold ${n(a.brake_wear_pct)>80?'text-red-400':n(a.brake_wear_pct)>60?'text-orange-400':'text-green-400'}`}>{a.brake_wear_pct||'—'}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{(a.last_inspection_date||'').slice(0,10)||'—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={n(dUntil(a.next_inspection_date))<=30&&n(dUntil(a.next_inspection_date))>=0?'text-yellow-400 font-bold':'text-slate-400'}>{(a.next_inspection_date||'').slice(0,10)||'—'}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {a.inspection_result?<span className={`text-xs font-medium ${a.inspection_result==='Conforme'?'text-green-400':a.inspection_result.includes('réserves')?'text-yellow-400':'text-red-400'}`}>{a.inspection_result}</span>:<span className="text-slate-500">—</span>}
                          </td>
                          <td className="px-3 py-2 text-red-400 text-right whitespace-nowrap">{a.repair_cost?ff(n(a.repair_cost)):'—'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={n(a.repair_duration_days)>60?'text-red-400 font-bold':'text-slate-300'}>{a.repair_duration_days||'—'}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.loss_type||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 max-w-40 truncate whitespace-nowrap" title={a.decommission_reason}>{a.decommission_reason||'—'}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.assigned_officer||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{a.assigned_email||'—'}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{a.purchase_date?.slice(0,10)||'—'}</td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${amortized?'bg-red-500/20 text-red-300':'bg-green-500/20 text-green-300'}`}>{amortized?'Amorti':'Actif'}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold border ${a.status==='Opérationnel'?'text-green-400 border-green-500/30':a.status==='En réparation'?'text-orange-400 border-orange-500/30':'text-red-400 border-red-500/30'}`}>{a.status||'—'}</span>
                          </td>
                          <td className="px-3 py-2">
                            <button onClick={()=>setQrAsset(a)}
                              className="flex items-center gap-1 px-2 py-1 rounded bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 transition-colors">
                              <QrCode className="w-3 h-3"/>QR
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length>50&&<p className="text-center text-slate-500 text-xs py-2">Affichage 50/{filtered.length} — affiner la recherche</p>}
              </div>
              <div className="px-4 py-2.5 border-t border-blue-900/40 flex items-center justify-between">
                <span className="text-slate-400 text-xs">{filtered.length} actifs · {filtered.length > 50 ? '50 affichés' : 'tous affichés'}</span>
                <button onClick={()=>{
                  const cols=['asset_id','asset_type','vehicle_registration','serial_number','category','year_manufactured','region','unit','assigned_city','assigned_precinct','status','condition','operational_since','current_mileage','operational_hours','fuel_type','fuel_capacity_l','last_fuel_date','last_fuel_quantity_l','last_maintenance_date','maintenance_cost_ytd','brake_wear_pct','last_inspection_date','next_inspection_date','inspection_result','repair_cost','repair_duration_days','loss_type','decommission_reason','assigned_officer','assigned_email','purchase_date','purchase_cost','asset_notes'];
                  const rows=filtered.map((a:any)=>cols.map(c=>`"${String(a[c]??'').replace(/"/g,'""')}"`).join(','));
                  const csv=[cols.join(','),...rows].join('\n');
                  const el=document.createElement('a');el.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);el.download=`nexus360_actifs_${new Date().toISOString().slice(0,10)}.csv`;el.click();
                }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs hover:bg-yellow-500/20 transition-colors">
                  <Download className="w-3 h-3"/>Export CSV complet
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Fleet tab removed — see Intelligence Logistique */}
        {tab==='fleet'&&(
          <motion.div key='fleet' initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0}}
            className='rounded-xl border border-blue-900/40 bg-slate-800/80 p-8 text-center'>
            <Truck className='w-10 h-10 text-blue-400 mx-auto mb-3'/>
            <p className='text-white font-semibold mb-1'>Gestion du parc automobile</p>
            <p className='text-slate-400 text-sm'>Disponible dans <span className='text-blue-300 font-bold'>Intelligence Logistique</span> — fiches individuelles, alertes maintenance, disponibilité temps réel.</p>
          </motion.div>
        )}

        {/* ══════ TAB: ANTI-DÉTOURNEMENTS ══════ */}
        {tab==='antifraud'&&(
          <motion.div key="antifraud" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {label:'Anomalies carburant (+30%)',value:assets.filter(a=>fuelAnomaly(a)>30).length,color:'text-red-400',bg:'bg-red-500/10',border:'border-red-500/30',icon:AlertTriangle},
                {label:'Kilométrage anormal (+30%)',value:assets.filter(a=>mileageAnomaly(a,catAvgMileage[a.category]||0)>30).length,color:'text-orange-400',bg:'bg-orange-500/10',border:'border-orange-500/30',icon:TrendingUp},
                {label:'Réparation >60 jours',value:assets.filter(longRepair).length,color:'text-yellow-400',bg:'bg-yellow-500/10',border:'border-yellow-500/30',icon:Clock},
              ].map((k,i)=>(
                <motion.div key={i} whileHover={{scale:1.02}} className={`rounded-xl p-4 border ${k.border} ${k.bg} flex items-center gap-3`}>
                  <k.icon className={`w-6 h-6 ${k.color}`}/>
                  <div><p className={`font-bold text-2xl ${k.color}`}>{k.value}</p><p className="text-slate-400 text-xs">{k.label}</p></div>
                </motion.div>
              ))}
            </div>

            <div className="rounded-xl border border-red-900/40 bg-slate-800/80 overflow-hidden">
              <div className="px-4 py-3 bg-slate-700/80 border-b border-red-900/40 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse"/>
                <span className="text-sm font-semibold text-white">Alertes détection anti-détournements ({fraudAlerts.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-red-900/30 bg-slate-700/60">
                      {['Actif','Type','Région','Anomalie','Détail','Kilométrage','Durée répa.','Responsable','Action'].map(h=>(
                        <th key={h} className="px-3 py-2 text-left text-red-300 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fraudAlerts.map((a,i)=>{
                      const fuel = fuelAnomaly(a);
                      const miles = mileageAnomaly(a, catAvgMileage[a.category]||0);
                      const repair = longRepair(a);
                      const anomaly = fuel>30?`Carburant +${fuel}%`:miles>30?`Kilométrage +${miles}%`:repair?`Réparation ${n(a.repair_duration_days)}j`:'Multi-anomalie';
                      const level = fuel>50||miles>50||n(a.repair_duration_days)>90?'CRITIQUE':fuel>30||miles>30||n(a.repair_duration_days)>60?'ÉLEVÉ':'MODÉRÉ';
                      return(
                        <motion.tr key={a.asset_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.03}}
                          className={`border-b border-red-900/20 hover:bg-red-800/10 ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-red-300 font-mono font-bold">{a.asset_id}</td>
                          <td className="px-3 py-2 text-white whitespace-nowrap">{a.asset_type}</td>
                          <td className="px-3 py-2 text-slate-300">{a.region}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full font-bold text-xs ${level==='CRITIQUE'?'bg-red-500/20 text-red-300':level==='ÉLEVÉ'?'bg-orange-500/20 text-orange-300':'bg-yellow-500/20 text-yellow-300'}`}>{level}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-300">{anomaly}</td>
                          <td className="px-3 py-2 text-slate-300">{n(a.current_mileage).toLocaleString()} km</td>
                          <td className={`px-3 py-2 font-bold ${n(a.repair_duration_days)>60?'text-red-400':'text-slate-300'}`}>{n(a.repair_duration_days)}j</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{a.assigned_officer}</td>
                          <td className="px-3 py-2">
                            <button onClick={()=>setQrAsset(a)} className="px-2 py-1 rounded bg-red-500/20 border border-red-500/30 text-red-300 text-xs hover:bg-red-500/30">Enquête</button>
                          </td>
                        </motion.tr>
                      );
                    })}
                    {fraudAlerts.length===0&&(
                      <tr><td colSpan={9} className="text-center text-slate-500 py-8">Aucune anomalie détectée</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fuel chart */}
            <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
              <h3 className="text-sm font-semibold text-white mb-3">Consommation carburant — Réel vs Attendu (top 15)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={filtered.filter(a=>n(a.fuel_expected_liters)>0).slice(0,15).map(a=>({
                  id:a.asset_id.slice(-4),
                  réel:n(a.fuel_consumption_liters).toFixed(0),
                  attendu:n(a.fuel_expected_liters).toFixed(0),
                  anomalie:fuelAnomaly(a)>30,
                }))} margin={{top:4,right:8,left:0,bottom:4}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
                  <XAxis dataKey="id" tick={{fill:'#94a3b8',fontSize:8}}/>
                  <YAxis tick={{fill:'#94a3b8',fontSize:8}}/>
                  <Tooltip content={<NexTip/>}/>
                  <Legend wrapperStyle={{fontSize:'9px'}}/>
                  <Bar dataKey="réel" name="Réel (L)" fill="#ef4444" radius={[3,3,0,0]}/>
                  <Bar dataKey="attendu" name="Attendu (L)" fill="#10b981" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {/* ══════ TAB: REGISTRE ARMEMENTS ══════ */}
        {tab==='weapons'&&(
          <motion.div key="weapons" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                {label:'Total armements',value:WEAPONS_MOCK.length,color:'text-purple-400',bg:'bg-purple-500/10',border:'border-purple-500/30'},
                {label:'En service (non restitués)',value:WEAPONS_MOCK.filter(w=>!w.returned).length,color:'text-blue-400',bg:'bg-blue-500/10',border:'border-blue-500/30'},
                {label:'Restitués dans les délais',value:WEAPONS_MOCK.filter(w=>w.returned).length,color:'text-green-400',bg:'bg-green-500/10',border:'border-green-500/30'},
                {label:'Alertes non restitués',value:weaponAlerts.length,color:'text-red-400',bg:'bg-red-500/10',border:'border-red-500/30'},
              ].map((k,i)=>(
                <div key={i} className={`rounded-xl p-3 border ${k.border} ${k.bg}`}>
                  <p className={`font-bold text-xl ${k.color}`}>{k.value}</p>
                  <p className="text-slate-400 text-xs mt-1">{k.label}</p>
                </div>
              ))}
            </div>

            {weaponAlerts.length>0&&(
              <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse"/>
                  <span className="text-red-300 font-bold text-sm">ALERTE — {weaponAlerts.length} arme(s) non restituée(s) hors délai</span>
                </div>
                {weaponAlerts.map(w=>(
                  <div key={w.id} className="flex items-center justify-between bg-red-500/10 rounded-lg px-3 py-2 text-xs mb-1">
                    <span className="text-red-300 font-bold">{w.serial}</span>
                    <span className="text-white">{w.type} — {w.issued_to}</span>
                    <span className="text-red-300">{Math.abs(dUntil(w.return_due))}j de retard</span>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-xl border border-purple-900/40 bg-slate-800/80 overflow-hidden">
              <div className="px-4 py-3 bg-slate-700/80 border-b border-purple-900/40 flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-400"/>
                <span className="text-sm font-semibold text-white">Registre sécurisé des armements — Attribution nominative</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-purple-900/30 bg-slate-700/60">
                      {['N° Série','Type','Marque/Modèle','Calibre','Attribué à','Unité','Région','Attribution','Échéance','Statut','Signature','État'].map(h=>(
                        <th key={h} className="px-3 py-2 text-left text-purple-300 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {WEAPONS_MOCK.map((w,i)=>{
                      const overdue = !w.returned && dUntil(w.return_due)<0;
                      const daysLeft = dUntil(w.return_due);
                      return(
                        <tr key={w.id} className={`border-b border-purple-900/20 hover:bg-purple-800/10 ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-purple-300 font-mono font-bold">{w.serial}</td>
                          <td className="px-3 py-2 text-white">{w.type}</td>
                          <td className="px-3 py-2 text-slate-300">{w.brand} {w.model}</td>
                          <td className="px-3 py-2 text-slate-400">{w.caliber}</td>
                          <td className="px-3 py-2 text-white whitespace-nowrap">{w.issued_to}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{w.unit}</td>
                          <td className="px-3 py-2 text-slate-400">{w.region}</td>
                          <td className="px-3 py-2 text-slate-400">{w.issue_date}</td>
                          <td className="px-3 py-2 text-slate-400">{w.return_due}</td>
                          <td className="px-3 py-2">
                            {w.returned?(
                              <span className="flex items-center gap-1 text-green-400 font-bold"><CheckCircle className="w-3 h-3"/>Restitué</span>
                            ):overdue?(
                              <span className="flex items-center gap-1 text-red-400 font-bold animate-pulse"><AlertTriangle className="w-3 h-3"/>Retard {Math.abs(daysLeft)}j</span>
                            ):(
                              <span className="flex items-center gap-1 text-blue-400"><Clock className="w-3 h-3"/>En service</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-1 text-green-400 text-xs"><Lock className="w-3 h-3"/>{w.signature}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${w.condition==='Excellent'?'text-green-400':w.condition==='Bon'?'text-blue-400':w.condition==='Moyen'?'text-yellow-400':'text-red-400'}`}>{w.condition}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* ====== TAB: COUTS & TCO ====== */}
        {tab==='costs'&&(
          <motion.div key="costs" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="space-y-6">

            {/* TOP: Optimisation Achats */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 py-2 border-b border-blue-900/40">
                <ShoppingCart className="w-4 h-4 text-yellow-400"/>
                <span className="text-sm font-bold text-yellow-300">Optimisation des Achats</span>
              </div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {label:'Économies potentielles (commandes groupées)',value:ff(totalProcurementSavings*1000),color:'text-green-400',bg:'bg-green-500/10',border:'border-green-500/30',icon:TrendingDown},
                {label:'Coût maintenance total YTD',value:ff(assets.reduce((s,a)=>s+n(a.maintenance_cost_ytd),0)),color:'text-yellow-400',bg:'bg-yellow-500/10',border:'border-yellow-500/30',icon:Wrench},
                {label:'Coût réparations total',value:ff(assets.reduce((s,a)=>s+n(a.repair_cost),0)),color:'text-red-400',bg:'bg-red-500/10',border:'border-red-500/30',icon:AlertTriangle},
              ].map((k,i)=>(
                <div key={i} className={`rounded-xl p-4 border ${k.border} ${k.bg} flex items-center gap-3`}>
                  <k.icon className={`w-6 h-6 ${k.color}`}/>
                  <div><p className={`font-bold text-lg ${k.color}`}>{k.value}</p><p className="text-slate-400 text-xs mt-1">{k.label}</p></div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Coûts maintenance vs réparation par catégorie (K FCFA/actif)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={procurementData} margin={{top:8,right:8,left:8,bottom:28}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
                    <XAxis dataKey="cat" tick={<WrappedTick/>} interval={0} tickLine={false} height={44} padding={{left:16,right:16}}/>
                    <YAxis tick={{fill:'#94a3b8',fontSize:8}}/>
                    <Tooltip content={<NexTip/>}/>
                    <Legend wrapperStyle={{fontSize:'9px'}} verticalAlign="top" align="right"/>
                    <Bar dataKey="avgMaint" name="Maintenance moy. (K F)" fill="#eab308" radius={[3,3,0,0]}/>
                    <Bar dataKey="avgRepair" name="Réparation moy. (K F)" fill="#ef4444" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden">
                <div className="px-4 py-3 bg-slate-700/60 border-b border-blue-900/40">
                  <span className="text-sm font-semibold text-white">Recommandations commandes groupées</span>
                </div>
                <div className="overflow-y-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-blue-900/30 bg-slate-700/60">
                        {['Catégorie','Actifs','Maint. moy.','Répa. moy.','Économies estimées','Recommandation'].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {procurementData.map((d,i)=>(
                        <tr key={i} className={`border-b border-blue-900/20 ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-white font-medium">{d.cat}</td>
                          <td className="px-3 py-2 text-slate-300 text-center">{d.count}</td>
                          <td className="px-3 py-2 text-yellow-400">{d.avgMaint}K F</td>
                          <td className="px-3 py-2 text-red-400">{d.avgRepair}K F</td>
                          <td className="px-3 py-2 text-green-400 font-bold">{d.savingsEstimate}K F</td>
                          <td className="px-3 py-2 text-slate-400">Commande groupée {d.count}+ unités</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </div>

            {/* DIVIDER */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-blue-900/40"/>
              <span className="text-slate-500 text-xs px-3">TCO par Actif</span>
              <div className="flex-1 h-px bg-blue-900/40"/>
            </div>

            {/* BOTTOM: TCO */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 py-2 border-b border-blue-900/40">
                <DollarSign className="w-4 h-4 text-cyan-400"/>
                <span className="text-sm font-bold text-cyan-300">TCO par Actif</span>
              </div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                {label:'TCO total parc',value:ff(assets.reduce((s,a)=>s+tco(a),0)),color:'text-blue-400',bg:'bg-blue-500/10',border:'border-blue-500/30'},
                {label:'TCO moyen par actif',value:ff(assets.length?assets.reduce((s,a)=>s+tco(a),0)/assets.length:0),color:'text-yellow-400',bg:'bg-yellow-500/10',border:'border-yellow-500/30'},
                {label:'TCO max — actif le plus coûteux',value:ff(assets.length?Math.max(...assets.map(tco)):0),color:'text-red-400',bg:'bg-red-500/10',border:'border-red-500/30'},
              ].map((k,i)=>(
                <div key={i} className={`rounded-xl p-4 border ${k.border} ${k.bg}`}>
                  <p className={`font-bold text-xl ${k.color}`}>{k.value}</p>
                  <p className="text-slate-400 text-xs mt-1">{k.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
                <h3 className="text-sm font-semibold text-white mb-3">TCO moyen par catégorie (M FCFA)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tcoByCategory} margin={{top:8,right:8,left:8,bottom:28}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
                    <XAxis dataKey="cat" tick={<WrappedTick/>} interval={0} tickLine={false} height={44} padding={{left:16,right:16}}/>
                    <YAxis tick={{fill:'#94a3b8',fontSize:8}}/>
                    <Tooltip content={<NexTip/>}/>
                    <Legend wrapperStyle={{fontSize:'9px'}} verticalAlign="top" align="right"/>
                    <Bar dataKey="purchase" name="Acquisition (M F)" stackId="a" fill="#3b82f6" radius={[0,0,0,0]}/>
                    <Bar dataKey="maint" name="Maintenance (M F)" stackId="a" fill="#eab308"/>
                    <Bar dataKey="repair" name="Réparation (M F)" stackId="a" fill="#ef4444" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 overflow-hidden">
                <div className="px-4 py-3 bg-slate-700/60 border-b border-blue-900/40">
                  <span className="text-sm font-semibold text-white">Top actifs par TCO — Arbitrage achat/réparation</span>
                </div>
                <div className="overflow-y-auto max-h-72">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-blue-900/30 bg-slate-700/60">
                        {['Actif','Type','Région','Acquisition','Maint.','Répa.','TCO Total','Arbitrage'].map(h=>(
                          <th key={h} className="px-3 py-2 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...filtered].sort((a,b)=>tco(b)-tco(a)).slice(0,20).map((a,i)=>{
                        const t = tco(a);
                        const pct = n(a.purchase_cost)>0?Math.round((n(a.repair_cost)+n(a.maintenance_cost_ytd)*3)/n(a.purchase_cost)*100):0;
                        const arbitrage = pct>80?'Remplacer':pct>50?'Reviser budget':'Conserver';
                        return(
                          <tr key={a.asset_id} className={`border-b border-blue-900/20 hover:bg-blue-800/10 ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                            <td className="px-3 py-2 text-blue-300 font-mono">{a.asset_id}</td>
                            <td className="px-3 py-2 text-white whitespace-nowrap">{a.asset_type}</td>
                            <td className="px-3 py-2 text-slate-400">{a.region}</td>
                            <td className="px-3 py-2 text-blue-400">{ff(n(a.purchase_cost))}</td>
                            <td className="px-3 py-2 text-yellow-400">{ff(n(a.maintenance_cost_ytd)*3)}</td>
                            <td className="px-3 py-2 text-red-400">{ff(n(a.repair_cost))}</td>
                            <td className="px-3 py-2 text-white font-bold">{ff(t)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{arbitrage}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            </div>

          </motion.div>
        )}

      </AnimatePresence>

      {/* QR Modal */}
      <AnimatePresence>
        {qrAsset&&<QRModal asset={qrAsset} onClose={()=>setQrAsset(null)}/>}
      </AnimatePresence>

      {/* Footer */}
      <div className="rounded-xl border border-blue-900/30 bg-slate-800/40 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-slate-400 text-xs italic">Gestion Intelligente du Parc — DGSN · NEXUS360</span>
        <span className="text-blue-300 text-xs font-medium">Actualisation automatique · 30s</span>
      </div>
    </div>
  );
};
